
import React, { useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, HelpCircle, Mail, Plane, FileText, Users, Clock, Shield, Phone, Globe } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";

const FAQ = () => {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  const faqItems = [
    {
      id: 1,
      icon: Plane,
      question: "¿Cómo empiezo a usar Migrante Amigo?",
      answer: "Solo entra al sitio, selecciona el tema que te interesa y escribe tu pregunta. Nuestro asistente virtual te dará una respuesta clara en segundos."
    },
    {
      id: 2,
      icon: Shield,
      question: "¿Mi información está protegida?",
      answer: "Sí. Migrante Amigo no guarda tus preguntas ni tu información personal. Toda la interacción es privada y segura."
    },
    {
      id: 3,
      icon: FileText,
      question: "¿Cuánto cuesta usar Migrante Amigo?",
      answer: "Puedes hacer preguntas de forma gratuita. Algunas funciones avanzadas pueden tener un costo más adelante, pero siempre se te notificará antes de pagar."
    },
    {
      id: 4,
      icon: Users,
      question: "¿Quién me ayuda cuando tengo dudas?",
      answer: "Las respuestas son generadas por nuestro asistente digital, entrenado con información confiable sobre procesos migratorios. No reemplaza el consejo de un abogado, pero te orienta de forma clara y útil."
    },
    {
      id: 5,
      icon: Globe,
      question: "¿Puedo usar Migrante Amigo si estoy fuera de los Estados Unidos?",
      answer: "Sí. Puedes acceder desde cualquier país mientras tengas conexión a internet."
    },
    {
      id: 6,
      icon: Users,
      question: "¿Cómo actualizo mi información personal?",
      answer: "Si creas una cuenta, podrás ingresar a tu perfil y actualizar tus datos en cualquier momento."
    },
    {
      id: 7,
      icon: Shield,
      question: "¿Puedo recibir orientación legal confiable?",
      answer: "Migrante Amigo ofrece orientación general basada en información pública. Si tu caso es complejo o urgente, te recomendamos contactar a un abogado de inmigración certificado."
    },
    {
      id: 8,
      icon: Phone,
      question: "¿Qué hago si olvidé mi contraseña?",
      answer: "En la pantalla de inicio de sesión, haz clic en \"¿Olvidaste tu contraseña?\" y sigue las instrucciones para restablecerla."
    },
    {
      id: 9,
      icon: Globe,
      question: "¿Puedo hablar con alguien en español?",
      answer: "¡Claro! Toda la plataforma está diseñada para hispanohablantes. No necesitas traducir ni cambiar el idioma."
    },
    {
      id: 10,
      icon: Shield,
      question: "¿Cómo protegen mis datos personales?",
      answer: "Usamos estándares de seguridad y privacidad para proteger tu información. No vendemos tus datos ni los compartimos con terceros."
    }
  ];

  const toggleCard = (id: number) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-cream font-sans flex flex-col">
      <Navigation />
      
      <div className="flex-grow container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-6">
              <HelpCircle className="text-teal h-8 w-8 mr-3" />
              <h1 className="text-2xl md:text-3xl font-semibold text-navy">
                Respuestas a tus preguntas frecuentes
              </h1>
            </div>
            <p className="text-coolGray text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
              Aquí resolvemos tus dudas más comunes sobre Migrante Amigo y cómo podemos ayudarte con tus trámites migratorios.
            </p>
          </div>
          
          {/* FAQ Cards */}
          <div className="space-y-6 mb-12">
            {faqItems.map((item) => {
              const IconComponent = item.icon;
              const isExpanded = expandedCard === item.id;
              
              return (
                <Card 
                  key={item.id}
                  className="cursor-pointer transition-all duration-300 hover:shadow-xl hover:bg-slate-50/50 border border-gray-100 shadow-md bg-white/95 backdrop-blur-sm"
                  onClick={() => toggleCard(item.id)}
                >
                  <CardContent className="p-5 md:p-6">
                    <div className="flex items-center justify-between min-h-[44px]">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center shadow-sm">
                            <IconComponent className="h-6 w-6 text-teal" />
                          </div>
                        </div>
                        <h3 className="text-lg md:text-xl font-medium text-navy leading-tight">
                          {item.question}
                        </h3>
                      </div>
                      
                      <div className="flex-shrink-0 ml-4">
                        <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                          <ChevronDown className="h-5 w-5 text-coolGray" />
                        </div>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="mt-6 animate-accordion-down">
                        <div className="mx-auto max-w-3xl">
                          <div className="p-6 bg-teal/5 rounded-xl border-l-4 border-teal shadow-sm">
                            <p className="text-coolGray leading-relaxed text-base text-center">
                              {item.answer}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Contact CTA Box */}
          <Card className="bg-gradient-to-br from-slate-50 to-blue-50/50 border border-blue-100/50 shadow-lg">
            <CardContent className="p-8 text-center">
              <h2 className="text-xl md:text-2xl font-semibold text-navy mb-4">
                ¿Aún tienes preguntas?
              </h2>
              <p className="text-coolGray text-base md:text-lg mb-6 max-w-2xl mx-auto leading-relaxed">
                Contáctanos directamente y con gusto te ayudaremos con tu proceso migratorio.
              </p>
              <Button 
                className="bg-teal hover:bg-teal-hover text-white px-8 py-3 rounded-xl text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                <Mail className="h-5 w-5 mr-2" />
                Contáctanos
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default FAQ;
